import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Iiso2Codes } from '../../models/enviroment-delivery-details.model';
import * as ValidationPatterns from '../../configurations/validations';
import { AddressUtils } from '../../utils/address.utils';
import { Subject } from 'rxjs';
import { CustomerFacade } from '../../facades/customer.facade';
import { takeUntil } from 'rxjs/operators';
import { EGlueResource, EStoreFeatures, EStoreTypes } from '../../configurations/common';
import { AppUtils } from '../../utils/app.utils';
import { layout } from './layout';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { ICustomerRequestData } from '../../models/guest.models';
import { ICustomerAddressPayloadData } from '../../models/customer.models';

@Component({
  selector: 'app-new-delivery-address-modal',
  templateUrl: './new-delivery-address-modal.component.html',
  styleUrls: ['./new-delivery-address-modal.component.scss'],
  standalone: false,
})
export class NewDeliveryAddressModalComponent implements OnInit, OnChanges, OnDestroy {
  isSparepartsStore: boolean = false;
  isCaStore: boolean = false;
  isUsStore: boolean = false;
  currentStoreId!: string;
  addressForm!: UntypedFormGroup;
  iso2Codes!: Iiso2Codes[];
  data!: ICustomerAddressPayloadData;
  maxLengthName: number = 50;
  maxLengthZipcode: number = 15;
  maxLengthCity: number = 35;
  maxLengthState: number = 35;
  maxLengthStreetAndNumber: number = 50;
  minLengthState: number = 2;
  minLengthCity: number = 3;
  auth0UserId: any;
  addNewAddressIsInProgress: boolean = false;
  saveAddressForLater: boolean = false;

  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  @Input() showModal!: boolean;
  @Input() isExternalQuoteCheckoutEnabled: boolean = false;
  @Input() externalQuoteCustomerRequestData!: ICustomerRequestData;

  @Output() closeModal: EventEmitter<any> = new EventEmitter<any>();
  @Output() newAddress: EventEmitter<any> = new EventEmitter<any>();

  private readonly formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  ngOnInit(): void {
    this.currentStoreId = AppUtils.getCurrentStore().storeId;
    this.isSparepartsStore = this.configurationFacade.isFeatureAvailable(EStoreFeatures.SPARE_PARTS);
    this.isCaStore = AppUtils.isStoreActive(EStoreTypes.CA);
    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.getIso2Codes();
    this.setMaxLengthForInputBasedOnCountry();
    this.initializeForm();
    this.selectAuth0User();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showModal.currentValue) {
      this.initializeForm();
    }
  }

  initializeForm(): void {
    const country: string | Iiso2Codes = this._countryMapping();
    this.addressForm = this.formBuilder.group({
      country: country,
      name: ['', [
        Validators.required,
        Validators.maxLength(this.maxLengthName),
        ValidationPatterns.noEmptySpaceOnTheBeginning,
      ]],
      streetAndNumber: ['', [
        Validators.required,
        Validators.maxLength(this.maxLengthName),
        ValidationPatterns.streetAndNumberPattern,
      ]],
      zipCode: ['', [
        Validators.required,
        Validators.maxLength(this.maxLengthZipcode),
        ValidationPatterns.zipCodeValidation(
          new RegExp(
            this.isCaStore
              ? ValidationPatterns.canadaZipCodeRegex
              : ValidationPatterns.zipCodeRegex),
          'generalZipCode',
        ),
      ]],
      city: ['', [
        Validators.required,
        Validators.minLength(this.minLengthCity),
        Validators.maxLength(this.maxLengthCity),
        ValidationPatterns.cityStatePattern,
      ]],
      state: ['', [
        ...(!this.isExternalQuoteCheckoutEnabled ? [Validators.required] : []),
        Validators.minLength(this.minLengthState),
        Validators.maxLength(this.maxLengthState),
        ValidationPatterns.cityStatePattern,
        ...(this.isSparepartsStore ? [ValidationPatterns.uppercase] : []),
      ]],
    });
  }

  resetForm(): void {
    this.closeModal.emit();
    this.addressForm.reset();
  }

  /*
    * Get iso2 codes for delivery address form based on the store type
    * @returns {void}
   */
  getIso2Codes(): void {
    if (this.isExternalQuoteCheckoutEnabled) {
      this.iso2Codes = [AddressUtils.getCountryForExternalQuoteCheckout(this.externalQuoteCustomerRequestData.countryIso2)];
    } else {
      this.iso2Codes = AddressUtils.getCountriesForDeliveryAddress();
    }
  }

  formIsValid(): boolean {
    return this.addressForm.status === 'VALID';
  }

  /**
   * Save new address from modal:
   * - if checkbox is checked, address will be saved via POST (postCustomerAddresses).
   * - if checkbox is unchecked, address will be saved only for current order flow.
   */
  saveAddress(): void {
    this.addNewAddressIsInProgress = true;
    this.createDataForPostNewAddress();

    if (this.saveAddressForLater) {
      this.customerFacade.beginAddCustomerAddressAction(`${this.auth0UserId}`, this.data);
    }

    this.constructNewAddress(this.data);
    this.addNewAddressIsInProgress = false;
    this.resetForm();
    this.saveAddressForLater = false;
  }

  createDataForPostNewAddress(): void {

    this.data = {
      data: {
        type: EGlueResource.ADDRESSES,
        attributes: {
          salutation: 'Ms',
          firstName: this.addressForm.value.name,
          lastName: 'empty',
          address1: this.addressForm.value.streetAndNumber,
          address2: this.addressForm.value.state,
          address3: '',
          phone: '',
          company: this.addressForm.value.name,
          country: this.addressForm.value.country.name,
          zipCode: this.addressForm.value.zipCode,
          city: this.addressForm.value.city,
          iso2Code: this.addressForm.value.country.value,
          isDefaultShipping: true,
          isDefaultBilling: false,
        },
      },
    };
  }

  setCountry(event: any): void {
    this.addressForm.patchValue({
      [event.key]: event,
    });
  }

  constructNewAddress(data: any): void {
    if (data?.data?.attributes) {
      const newAddress: any = {
        name: AddressUtils.createAddressString(data.data.attributes),
        value: {...data.data.attributes, isCustom: true},
      };

      this.newAddress.emit(newAddress);
    }
  }

  selectAuth0User(): void {
    this.customerFacade.getAuth0CustomerFromStore().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(user => {
      if (user) {
        this.auth0UserId = user['https://login-shs.siemens-healthineers.com/gid']?.replace('"', '');
      }
    });
  }

  setMaxLengthForInputBasedOnCountry(): void {
    if (this.isSparepartsStore) {
      this.maxLengthName = 35;
      this.maxLengthZipcode = 10;
      this.maxLengthCity = 30;
      this.maxLengthState = 2;
      this.minLengthCity = 2;
      this.maxLengthStreetAndNumber = 35;
    }

    if (this.isCaStore) {
      this.maxLengthZipcode = 7;
    }
  }

  /**
   * Returns the CSS column class for a form field at the given index,
   * based on the current store's layout configuration.
   *
   * @param index - The index of the form field in the layout.
   * @returns The CSS class string, or an empty string if not defined.
   */
  getColumn(index: number): string {
    if (Object.hasOwn(layout, this.currentStoreId)) {
      return (layout as Record<string, { class: string; order: string }[]>)[this.currentStoreId][index].class;
    } else {
      return layout.default[index].class;
    }
  }

  /**
   * Returns the CSS order value for a form field at the given index,
   * based on the current store's layout configuration.
   *
   * @param index - The index of the form field in the layout.
   * @returns The CSS order string, or an empty string if not defined.
   */
  getOrder(index: number): string {
    if (Object.hasOwn(layout, this.currentStoreId)) {
      return (layout as Record<string, { class: string; order: string }[]>)[this.currentStoreId][index].order;
    } else {
      return layout.default[index].order;
    }
  }

  /**
   * Get custom zip code validator for delivery address form
   */
  getCustomZipCodeValidator(): void {
    const generalZipCodeValidator: ValidatorFn = ValidationPatterns.zipCodeValidation(
      new RegExp(
        this.isCaStore
          ? ValidationPatterns.canadaZipCodeRegex
          : ValidationPatterns.zipCodeRegex),
      'generalZipCode',
    );

    const puertoRicoZipCodeValidator: ValidatorFn = ValidationPatterns.zipCodeValidation(
      new RegExp(ValidationPatterns.puertoRicoZipCodeRegex), 'puertoRicoZipCode',
    );

    if (this.isUsStore && this.addressForm?.value?.state == ValidationPatterns.puertoRicoState) {
      this.addressForm.controls['zipCode'].setValidators([puertoRicoZipCodeValidator, Validators.required]);
    } else {
      this.addressForm.controls['zipCode'].setValidators([generalZipCodeValidator, Validators.required]);
    }

    this.addressForm.controls['zipCode'].setValue(this.addressForm?.value?.zipCode);
  }

  /**
   * Map country for delivery address form
   *
   * @returns {string | Iiso2Codes}
   * @private
   */
  private _countryMapping(): string | Iiso2Codes {
    let country: string | Iiso2Codes = this.iso2Codes.find(i => i.isDefault) || '';
    if (this.isExternalQuoteCheckoutEnabled) {
      country = this.iso2Codes.find(i => i.value === this.externalQuoteCustomerRequestData.countryIso2) || this.iso2Codes.find(i => i.isDefault) || '';
    }
    return country;
  }
}
